import { type BoxProps, Flex, Text, Box } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type ReactNode } from "react";
import { Rule } from "ui/rule";

type RuleAndTextCtaProps = {
  /**
   * Determines whether or not the rule is hidden.
   */
  hasRuleHidden?: boolean;

  /**
   * Which theme to use for rule and text colors.
   *
   * @default "light"
   * */
  theme?: Theme;

  /**
   * Use a `DirectionalButton` as the CTA.
   */
  action?: ReactNode;
} & BoxProps;

export function RuleAndTextCta({
  theme = "light",
  hasRuleHidden = false,
  action,
  children,
  ...boxProps
}: RuleAndTextCtaProps) {
  const textColor = `brand.text.${theme}`;

  return (
    <Flex flexDir="column" gap={{ base: "big", md: "tiny" }} color={textColor} {...boxProps}>
      {!hasRuleHidden && <Rule theme={theme} />}

      <Flex
        alignItems="start"
        justifyContent="space-between"
        flexDir={{ base: "column", md: "row" }}
        gap={{ base: "big", lg: "huge" }} // Use "big" instead of "bigger" from Figma, since spacing is different with the Nuckle web font
      >
        <Text
          whiteSpace="pre-wrap"
          textStyle={{ base: "headline2", lg: "headline1" }}
          py={{ md: "smallest" }} // Add vertical padding in desktop/row layout so the text properly aligns with the top of the button elements
          maxW={{ md: "75%" }} // Keep text from getting too wide in desktop/row layout
        >
          {children}
        </Text>

        <Box
          flexShrink={0}
          sx={{
            "& button, & a": {
              pl: 0, // Remove padding for proper left alignment in mobile/column layout
            },
          }}
        >
          {action}
        </Box>
      </Flex>
    </Flex>
  );
}
